import type { Style } from '../types/style.types'

/**
 * Merges styles according to the hierarchy: Team -> Event -> Page
 * Later styles override earlier ones, but only for non-null values
 */
export function mergeStyles(
  teamStyle?: Style | null,
  eventStyle?: Style | null,
  pageStyle?: Style | null
): Style | null {
  if (!teamStyle && !eventStyle && !pageStyle) {
    return null
  }

  const merged: Style = {
    id: 'merged-style', // This is a computed style, not from DB
  }

  // Apply styles in order: team -> event -> page
  const stylesToMerge = [teamStyle, eventStyle, pageStyle].filter(Boolean) as Style[]
  
  for (const style of stylesToMerge) {
    Object.keys(style).forEach(key => {
      const value = style[key as keyof Style]
      if (value !== undefined && value !== null) {
        (merged as any)[key] = value
      }
    })
  }

  return merged
}

/**
 * Gets the source of a style property by checking the hierarchy
 */
export function getStylePropertySource(
  property: keyof Style,
  teamStyle?: Style | null,
  eventStyle?: Style | null,
  pageStyle?: Style | null
): 'team' | 'event' | 'page' | null {
  // Check in reverse order (page -> event -> team) since we want the actual source
  if (pageStyle?.[property] !== undefined && pageStyle[property] !== null) {
    return 'page'
  }
  if (eventStyle?.[property] !== undefined && eventStyle[property] !== null) {
    return 'event'
  }
  if (teamStyle?.[property] !== undefined && teamStyle[property] !== null) {
    return 'team'
  }
  return null
}

/**
 * Checks if a style property is set in any of the style sources
 */
export function hasStyleProperty(
  property: keyof Style,
  teamStyle?: Style | null,
  eventStyle?: Style | null,
  pageStyle?: Style | null
): boolean {
  return getStylePropertySource(property, teamStyle, eventStyle, pageStyle) !== null
}

/**
 * Gets the background image URL with proper transformations
 * Handles both regular URLs and remote URLs with legacy prefix
 */
export function getBackgroundImageUrl(style: Style | null): string | null {
  if (!style) return null
  
  // Check various possible property names for background image
  // Prefer backgroundUrl over backgroundRemoteUrl
  if (style.backgroundUrl) {
    return style.backgroundUrl
  }
  
  // Check for backgroundImageUrl (from HAML transformation)
  if ((style as any).backgroundImageUrl) {
    return (style as any).backgroundImageUrl
  }
  
  if (style.backgroundRemoteUrl) {
    // If the remote URL already includes 'images/subtle-patterns/', use it as-is
    // Otherwise, add the /legacy/ prefix for backward compatibility
    if (style.backgroundRemoteUrl.includes('images/subtle-patterns/')) {
      return `/${style.backgroundRemoteUrl}`
    }
    return `/legacy/${style.backgroundRemoteUrl}`
  }
  
  return null
}

/**
 * Gets the logo URL with proper transformations
 */
export function getLogoUrl(style: Style | null): string | null {
  if (!style) return null
  
  if (style.logoUrl) {
    return style.logoUrl
  }
  
  if (style.logoRemoteUrl) {
    return `/legacy/${style.logoRemoteUrl}`
  }
  
  return null
}

/**
 * Gets the favicon URL with proper transformations
 */
export function getFaviconUrl(style: Style | null): string | null {
  if (!style) return null
  
  if (style.faviconUrl) {
    return style.faviconUrl
  }
  
  if (style.faviconRemoteUrl) {
    return `/legacy/${style.faviconRemoteUrl}`
  }
  
  return null
}

/**
 * Converts style properties to CSS variables
 */
export function styleToCSSVariables(style: Style | null): Record<string, string> {
  if (!style) return {}
  
  const cssVars: Record<string, string> = {}
  
  // Color mappings
  if (style.backgroundColor) cssVars['--style-background-color'] = style.backgroundColor
  if (style.headerColor) cssVars['--style-header-color'] = style.headerColor
  if (style.bodyColor) cssVars['--style-body-color'] = style.bodyColor
  if (style.linkColor) cssVars['--style-link-color'] = style.linkColor
  if (style.accentColor) cssVars['--style-accent-color'] = style.accentColor
  if (style.sidebarBackgroundColor) cssVars['--style-sidebar-bg-color'] = style.sidebarBackgroundColor
  if (style.sidebarTextColor) cssVars['--style-sidebar-text-color'] = style.sidebarTextColor
  
  // Other properties
  if (style.font) cssVars['--style-font-family'] = style.font
  if (style.borderRadius !== undefined && style.borderRadius !== null) {
    cssVars['--style-border-radius'] = `${style.borderRadius}px`
  }
  
  return cssVars
}

/**
 * Generates background CSS properties from style
 */
export function getBackgroundCSS(style: Style | null): React.CSSProperties {
  if (!style) return {}
  
  const css: React.CSSProperties = {}
  const backgroundUrl = getBackgroundImageUrl(style)
  
  if (backgroundUrl) {
    css.backgroundImage = `url(${backgroundUrl})`
    
    // Handle background tiling
    if (style.backgroundTiled) {
      css.backgroundRepeat = 'repeat'
      css.backgroundSize = 'auto'
      css.backgroundPosition = '0 0'
    } else {
      css.backgroundRepeat = 'no-repeat'
      
      // Handle background position
      switch (style.backgroundPosition) {
        case 'left':
          css.backgroundPosition = 'left center'
          css.backgroundSize = 'auto'
          break
        case 'right':
          css.backgroundPosition = 'right center'
          css.backgroundSize = 'auto'
          break
        case 'center':
          css.backgroundPosition = 'center center'
          css.backgroundSize = 'auto'
          break
        case 'expand':
          css.backgroundPosition = 'center center'
          css.backgroundSize = 'cover'
          break
        default:
          css.backgroundPosition = 'center center'
          css.backgroundSize = 'auto'
      }
    }
  }
  
  if (style.backgroundColor) {
    css.backgroundColor = style.backgroundColor
  }
  
  return css
}

/**
 * Cleanup function for when styles change or component unmounts
 */
export function cleanupStyleEffects(): void {
  // Remove any dynamically added style elements
  const dynamicStyles = document.querySelectorAll('style[data-style-provider]')
  dynamicStyles.forEach(el => el.remove())
  
  // Reset CSS variables on root
  const root = document.documentElement
  const cssVars = [
    '--style-background-color',
    '--style-header-color',
    '--style-body-color',
    '--style-link-color',
    '--style-accent-color',
    '--style-sidebar-bg-color',
    '--style-sidebar-text-color',
    '--style-font-family',
    '--style-border-radius',
  ]
  
  cssVars.forEach(varName => {
    root.style.removeProperty(varName)
  })
}